import React, { useMemo, useRef } from 'react';

/**
 * TaxInvoice — A5 Print-Optimised (original content preserved exactly)
 * Props:
 *  - header         : order header object
 *  - items          : array of order items
 *  - customer       : customer object
 *  - settlementInfo : { payment_mode_label, settlement_date, remarks }
 *  - formatDate     : (dateStr) => string
 *  - formatCurr     : (num)     => string
 */
const TaxInvoice = ({ header, items = [], customer, settlementInfo = null, formatDate, formatCurr }) => {
    const printRef = useRef(null);
    const fmt =
        formatDate ||
        ((d) => {
            if (!d) return '';
            const dt = new Date(d);
            return `${String(dt.getDate()).padStart(2, '0')}-${String(dt.getMonth() + 1).padStart(2, '0')}-${dt.getFullYear()}`;
        });

    const curr =
        formatCurr ||
        ((n) =>
            parseFloat(n || 0).toLocaleString('en-IN', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }));

    const totals = useMemo(() => {
        const subTotal = items.reduce((acc, i) => acc + (parseFloat(i.price || i.Value) || 0), 0);
        const taxRate = 0.05; //{curr(Math.round(totals.subTotal, 0))} * taxRate;
        const totalTax = subTotal * taxRate; //curr(Math.round(totals.subTotal + totals.cgst + totals.sgst - totals.grandTotal, 2))
        const rawGrand = subTotal + totalTax;
        const roundedGrand = Math.round(rawGrand, 2);
        console.log(rawGrand);
        console.log(roundedGrand);
        return {
            subTotal,
            cgst: totalTax / 2,
            sgst: totalTax / 2,
            roundOff: rawGrand - roundedGrand,
            grandTotal: roundedGrand
        };
    }, [items]);

    return (
        <>
            <link rel="preconnect" href="https://fonts.googleapis.com" />
            <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
            {/* <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;900&family=IBM+Plex+Mono:wght@400;500;600&display=swap" rel="stylesheet" /> */}

            <style>{`
               *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

                /* ── Screen wrapper ── */
                .ti-wrap {
        display: flex;
        justify-content: center;
        align-items: flex-start;
        min-height: 100vh;
        background: #d6d0c8;
        padding: 28px 16px 48px;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif;
    }

                /* ── A5 card: 148×210 mm → 559×794 px at 96 dpi ── */
                .ti-invoice {
        width: 559px;
        min-height: 794px;
        background: #fff;
        color: #000;
        padding: 28px 32px 24px;
        box-shadow: 0 2px 8px rgba(0,0,0,.1), 0 16px 48px rgba(0,0,0,.18);
        display: flex;
        flex-direction: column;
    }

                /* ── Title ── */
                .ti-title {
                    text-align: center;
                    margin-bottom: 6px;
                }
                .ti-title h2 {
                    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji";
                    font-size: 22px;
        font-weight: 900;
        letter-spacing: 4px;
        text-transform: uppercase;
        margin-bottom: 6px;
                }

                /* ── Dividers ── */
                .ti-dotted {
                    border: none;
        border-top: 2px dotted #000;
        margin: 1px 0;
        width: 100%;
                }

                /* ── Meta row ── */
                .ti-meta {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    margin: 1px 0 2px;
                }
               ti-meta-left p, .ti-meta-right p {
        font-size: 15px;
        font-weight: bold;
        margin: 2px 0;
        line-height: 1.5;
    }
                .ti-meta-right { text-align: right; }

                .ti-bill-no {
                    font-size: 15px;
                    font-weight: bold;
                    margin: 2px 0 4px;
                }

                /* ── Items table ── */
                .ti-table {
        width: 100%;
        border-collapse: collapse;
        margin: 1px 0;
    }
                .ti-table thead th {
                    font-size: 13px;          /* Reduced from 14px */
        font-weight: bold;
        padding: 2px 4px !important;
        text-align: left;
        border-bottom: 2px dotted #000;
        letter-spacing: 0.3px;
                }
                .ti-table thead th.center { text-align: center; }
                .ti-table thead th.right  { text-align: right; }

                .ti-table tbody td {
                    font-size: 14px;          /* Reduced from 15px to fit more rows */
        font-weight: bold;
        padding: 1px 4px;         /* Tightened from 2px to 1px */
        vertical-align: middle;
        line-height: 1.1;
                }
                .ti-table .center { text-align: center; }
    .ti-table .right  { text-align: right; }

                /* ── Totals section ── */
                .ti-totals-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    margin-top: 6px;
                    gap: 12px;
                }
                .ti-codes {
                    font-size: 11px;
                    line-height: 1.8;
                    color: #000;
                }

                .ti-amounts {
                    min-width: 240px;
                }
                .ti-amt-line {
                    display: flex;
        justify-content: space-between;
        font-size: 15px;
        padding: 1.5px 0;
        font-weight: bold;
                }
                .ti-amt-line span:last-child { text-align: right; min-width: 90px; }
                .ti-amt-line.bold {
                    font-size: 15px;
                    font-weight: bold!important;
                    padding-top:0px;
                }

                /* ── Settlement box ── */
                .ti-settlement-box {
                    background: #f0f7ff;
                    border: 1px solid #2196F3;
                    border-radius: 4px;
                    padding: 10px 14px;
                    margin-top: 10px;
                    font-size: 12px;
                }
                .ti-settlement-box h4 {
                    margin: 0 0 7px 0;
                    color: #1565C0;
                    font-size: 11px;
                    letter-spacing: 1.5px;
                    text-transform: uppercase;
                }
                .ti-s-row {
                    display: flex;
                    justify-content: space-between;
                    margin: 3px 0;
                }
                .ti-s-label { color: #444; }
                .ti-s-value { font-weight: 600; }

                /* ── Footer signature ── */
                .ti-footer {
                    margin-top: 0;
                    padding-top: 2px;
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-end;
                }
                .ti-footer-codes {
                    font-size: 16px;
        line-height: 1.2;
        font-weight: bold;
        margin-top: 10px;
                }
                .ti-sig {
                    text-align: center;
                    min-width: 140px;
                }
                .ti-sig .ti-thank {
        font-size: 14px;
        font-weight: 700;
        margin-bottom: 32px;
    }
                .ti-sig .ti-manager {
                   border-top: 1px solid #000;
        padding-top: 4px;
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
                }
                    .ti-meta-left p {
                    margin: 0px!important;
                    }
                .ti-meta-left p:last-child {
                    margin-top: 4px;
                }
                .ti-meta-right p {
                    margin: 0;
                    font-size: 12px;
                    line-height: 1.2;
                    font-weight: 700;
                    text-transform: uppercase;
                }
                .ti-meta-right p:last-child {
                    margin-top: 4px;
                    }
                /* ══ PRINT STYLES — A5 only ════════════════════ */
                @media print {
                    @page {
                        size: A5 portrait;
                        margin: 10mm;
                    }

                    *, *::before, *::after {
                        -webkit-print-color-adjust: exact !important;
                        print-color-adjust: exact !important;
                    }

                    html, body {
                        width: 148mm !important;
                        height: auto !important;
                        min-height: unset !important;
                        margin: 0 !important;
                        padding: 0 !important;
                        background: #fff !important;
                        overflow: visible !important;
                    }

                    /* Flatten wrapper */
                    .ti-wrap { display: block !important; padding: 0 !important; background: none !important; }

                    /* Strip the screen card sizing — @page margin handles spacing */
                    .ti-invoice {
                        width: 100% !important; 
            min-height: unset !important; 
            height: auto !important; 
            box-shadow: none !important; 
            padding: 0 !important;
                    }

                    /* Kill <br> spacers that push content down */
                    .ti-invoice > div > br { display: none !important; }

                    /* No row splits across pages */
                    .ti-table tbody tr {
                        page-break-inside: avoid;
                        break-inside: avoid;
                    }

                   
                }
            `}</style>

            <div ref={printRef} className="printable-content">
                <div className="">
                    {/* ── Title ── */}
                    <br />
                    <br />
                    <br />
                    <br />

                    <div className="ti-title">
                        <h2>Tax Invoice</h2>
                    </div>

                    <hr className="ti-dotted" />

                    {/* ── Customer & Date ── */}
                    <div className="ti-meta">
                        <div className="ti-meta-left">
                            <p>
                                <strong>Customer Name : {header?.customer_name}</strong>
                            </p>
                            <p>
                                <strong>GSTIN : {header?.customer_gst}</strong>
                            </p>
                        </div>
                    </div>
                    <div className="ti-meta">
                        <div className="ti-meta-left">
                            <p className="ti-bill-no">
                                <strong>Bill # : {header?.in_no}</strong>
                            </p>
                        </div>
                        <div className="ti-meta-right">
                            <p>
                                <strong>Date : {fmt(header?.invoice_date)}</strong>
                            </p>
                        </div>
                    </div>

                    <hr className="ti-dotted" />

                    {/* ── Items table ── */}
                    <table className="ti-table">
                        <thead>
                            <tr>
                                <th style={{ width: '40px' }}>Sl.no</th>
                                <th>Item Name</th>
                                <th className="center" style={{ width: '50px' }}>
                                    Qty
                                </th>
                                <th className="right" style={{ width: '90px' }}>
                                    Rate
                                </th>
                                <th className="right" style={{ width: '100px' }}>
                                    Value
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((item, idx) => (
                                <tr key={idx}>
                                    <td>{idx + 1}</td>
                                    <td>{item.product_name || item.ItemName}</td>
                                    <td className="center">{item.quantity ?? item.Qty ?? '0'}</td>
                                    <td className="right">{curr(item.basic_rate)}</td>
                                    <td className="right">{curr(item.price, 0)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <hr className="ti-dotted" />

                    {/* ── Totals ── */}
                    <div className="ti-totals-row">
                        <div className="ti-amounts" style={{ marginLeft: 'auto' }}>
                            <div className="ti-amt-line bold">
                                <span>Total amount :</span>
                                <span> {curr(totals.subTotal, 0)}</span>
                            </div>
                            <div className="ti-amt-line bold">
                                <span>CGST (2.5%) :</span>
                                <span>{curr(totals.cgst)}</span>
                            </div>
                            <div className="ti-amt-line bold">
                                <span>SGST (2.5%) :</span>
                                <span>{curr(totals.sgst)}</span>
                            </div>
                            <div className="ti-amt-line bold">
                                <span>Round Off :</span>
                                <span>{curr(totals.roundOff)}</span>
                            </div>
                            <hr className="ti-dotted" style={{ margin: '4px 0' }} />
                            <div className="ti-amt-line bold">
                                <span>Net Amount :</span>
                                <span>{curr(totals.grandTotal)}</span>
                            </div>
                        </div>
                    </div>

                    {/* ── Settlement ── */}
                    {settlementInfo && (
                        <div className="ti-settlement-box no-print-hide">
                            <h4>Payment Settlement Details</h4>
                            <div className="ti-s-row">
                                <span className="ti-s-label">Payment Mode :</span>
                                <span className="ti-s-value">{settlementInfo.payment_mode_label || '—'}</span>
                            </div>
                            <div className="ti-s-row">
                                <span className="ti-s-label">Settlement Date :</span>
                                <span className="ti-s-value">{fmt(settlementInfo.settlement_date)}</span>
                            </div>
                            {settlementInfo.remarks && (
                                <div className="ti-s-row">
                                    <span className="ti-s-label">Remarks :</span>
                                    <span className="ti-s-value">{settlementInfo.remarks}</span>
                                </div>
                            )}
                        </div>
                    )}

                    <hr className="ti-dotted" style={{ marginTop: '4px' }} />

                    {/* ── Footer ── */}
                    <div className="ti-footer">
                        <div className="ti-footer-codes">
                            <div>
                                <strong>GSTIN : 29AAEFS3193A1ZA</strong>
                            </div>
                            <div>
                                <strong>SAC CODE : 996334</strong>
                            </div>
                            <div>
                                <strong>FSSAI NO : 11221331001104</strong>
                            </div>
                            <div>
                                <strong>PAN NO : AAEFS3193A</strong>
                            </div>
                        </div>
                        <div className="ti-sig">
                            <div className="ti-thank">THANK YOU</div>
                            <div className="ti-manager">Manager</div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default TaxInvoice;
